import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

type PreviewKind = 'pdf' | 'image' | 'none';

@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.css']
})
export class DocumentViewerComponent implements OnInit, OnDestroy {
  document: any;
  loading = false;
  loadError: string | null = null;

  /** Raw blob object URL — revoked on destroy */
  private blobUrl: string | null = null;
  /** Sanitized URL for iframe/img binding (required for blob: URLs in Angular) */
  safePreviewUrl: SafeResourceUrl | null = null;

  constructor(
    public dialogRef: MatDialogRef<DocumentViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private sanitizer: DomSanitizer
  ) {
    this.document = data.document;
  }

  ngOnInit() {
    this.loadDocument();
  }

  ngOnDestroy() {
    this.revokeBlobUrl();
  }

  get previewKind(): PreviewKind {
    if (this.isPdf()) {
      return 'pdf';
    }
    if (this.isImage()) {
      return 'image';
    }
    return 'none';
  }

  loadDocument() {
    this.loading = true;
    this.loadError = null;
    this.revokeBlobUrl();

    this.apiService.downloadDocument(this.document.id).subscribe({
      next: async (blob: Blob) => {
        try {
          await this.applyBlobPreview(blob);
        } catch (e) {
          console.error('Error preparing preview:', e);
          this.loadError = 'Could not display this file in the browser.';
          this.toastrService.error(this.loadError);
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading document:', err);
        this.loadError = err.error?.error || 'Failed to load document';
        this.toastrService.error(this.loadError);
        this.loading = false;
      }
    });
  }

  private async applyBlobPreview(blob: Blob): Promise<void> {
    if (await this.blobLooksLikeJsonError(blob)) {
      throw new Error('Server returned an error instead of the file');
    }

    const mime = this.resolvePreviewMimeType(blob);
    const typedBlob =
      !blob.type || blob.type === 'application/octet-stream'
        ? new Blob([blob], { type: mime })
        : blob;

    this.blobUrl = URL.createObjectURL(typedBlob);
    this.safePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
  }

  /** API errors often arrive as application/json even with responseType blob */
  private async blobLooksLikeJsonError(blob: Blob): Promise<boolean> {
    if (blob.type && blob.type.includes('json')) {
      return true;
    }
    if (blob.size > 0 && blob.size < 4096) {
      const head = await blob.slice(0, 80).text();
      const trimmed = head.trim();
      if (trimmed.startsWith('{') && trimmed.includes('"error"')) {
        return true;
      }
    }
    return false;
  }

  private resolvePreviewMimeType(blob: Blob): string {
    const fromRecord = (this.document.file_type || '').trim();
    if (fromRecord && fromRecord !== 'application/octet-stream') {
      return fromRecord;
    }
    const ext = (this.document.file_extension || '').toLowerCase().replace(/^\./, '');
    const map: Record<string, string> = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml',
    };
    if (ext && map[ext]) {
      return map[ext];
    }
    if (blob.type && blob.type !== 'application/octet-stream') {
      return blob.type;
    }
    return 'application/octet-stream';
  }

  isPdf(): boolean {
    const type = (this.document.file_type || '').toLowerCase();
    const ext = (this.document.file_extension || '').toLowerCase();
    const name = (this.document.file_name || '').toLowerCase();
    return type.includes('pdf') || ext === '.pdf' || ext === 'pdf' || name.endsWith('.pdf');
  }

  isImage(): boolean {
    const type = (this.document.file_type || '').toLowerCase();
    if (type.startsWith('image/')) {
      return true;
    }
    const ext = (this.document.file_extension || '').toLowerCase().replace(/^\./, '');
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  }

  canViewInBrowser(): boolean {
    return this.previewKind !== 'none';
  }

  download() {
    this.apiService.downloadDocument(this.document.id).subscribe({
      next: (blob: Blob) => {
        const mime = this.resolvePreviewMimeType(blob);
        const typedBlob =
          !blob.type || blob.type === 'application/octet-stream'
            ? new Blob([blob], { type: mime })
            : blob;
        const url = window.URL.createObjectURL(typedBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.document.file_name;
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastrService.success('Document downloaded successfully');
      },
      error: (err: any) => {
        console.error('Error downloading document:', err);
        this.toastrService.error(err.error?.error || 'Failed to download document');
      }
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  close() {
    this.revokeBlobUrl();
    this.dialogRef.close();
  }

  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    this.safePreviewUrl = null;
  }
}
